import { FormControl, FormHelperText, InputLabel, MenuItem, Select, Stack, TextField, Typography } from "@mui/material";
import React from "react";
import { useHistory, useLocation } from "react-router-dom";
import { DialogPrompt, DialogPromptApi, DialogPromptConversationListenSpeak, DialogPromptConversationReadSpeak, DialogPromptConversationReadWithBlankSpeak, DialogPromptConversationRelated, DialogPromptConversationSpeakOnly, IDialogPromptSentence, SPEAKER_NAME_A, SPEAKER_NAME_B } from "../../api/DialogPromptApi";
import { DialogPromptConversationApi, IDialogPromptConversation } from "../../api/DialogPromptConversationApi";
import AppBarContext from '../../context/AppBarContext';
import { useConversationPinyin } from "../../hook/useConversationPinyin";
import { handleErrorMessage } from '../ErrorDialog';

interface DialogPromptConversationRelatedFormProps {
    oldDialogPromptId: number;
    setNewDialogPrompt: React.Dispatch<React.SetStateAction<DialogPrompt | null>>;
    setNewDialogPromptConversation: React.Dispatch<React.SetStateAction<IDialogPromptConversation | null>>;
    id: number;
    dialogId: number;
    orderIndex: number;
    isTeacher: boolean;
    prompt: string;
    isNoteToStudent: boolean;
    isHidden: boolean;
    yesResponse: string;
    noResponse: string;
    dialogPromptClassName: string;
}

export const DialogPromptConversationRelatedForm: React.FC<DialogPromptConversationRelatedFormProps> = ({
    oldDialogPromptId, setNewDialogPrompt, setNewDialogPromptConversation,
    id, dialogId, orderIndex, isTeacher, prompt, isNoteToStudent, isHidden, yesResponse, noResponse, dialogPromptClassName }) => {

    const { setErrorMessage } = React.useContext(AppBarContext)
    const history = useHistory();
    const location = useLocation();

    const [oldDialogPrompt, setOldDialogPrompt] = React.useState<DialogPromptConversationRelated | null>(null)
    const [dialogPromptConversationArray, setDialogPromptConversationArray] = React.useState<IDialogPromptConversation[]>([])
    const [dialogPromptConversationIdSelected, setDialogPromptConversationIdSelected] = React.useState<number>(-1);
    const {
        conversationText,
        conversationPinyin,
        conversationPinyinList,
        conversationErrorText,
        setConversationFields,
        clearConversationFields,
        clearConversationErrors,
        handleConversationTextChange,
        handleConversationPinyinChange,
    } = useConversationPinyin({ history, location, setErrorMessage });
    const [conversationEnabled, setConversationEnabled] = React.useState(true);

    const [dialogPromptRequiredError, setDialogPromptRequiredError] = React.useState(false);
    const [sentenceBlankList, setSentenceBlankList] = React.useState<string[]>([]);
    const [sentenceBlankErrorList, setSentenceBlankErrorList] = React.useState<string[]>([]);
    const initializedConversationKeyRef = React.useRef<string | null>(null);

    const upsertDialogPromptConversation = React.useCallback((conversation: IDialogPromptConversation) => {
        setDialogPromptConversationArray((currentList) => {
            const existingIndex = currentList.findIndex((item) => item.id === conversation.id);
            if (existingIndex >= 0) {
                const nextList = currentList.slice();
                nextList[existingIndex] = conversation;
                return nextList.sort((a, b) => b.id - a.id);
            }
            return [conversation, ...currentList].sort((a, b) => b.id - a.id);
        });
    }, []);

    const getOrderIndexKey = React.useCallback((sentenceList: IDialogPromptSentence[]) => (
        sentenceList
            .map((sentence) => sentence.order_index)
            .filter((orderIndex) => orderIndex != null)
            .map((orderIndex) => Number(orderIndex))
            .sort((a, b) => a - b)
            .join('|')
    ), []);

    const getSavedBlankSentence = React.useCallback((
        savedSentenceList: IDialogPromptSentence[],
        conversationSentence: IDialogPromptSentence,
        index: number,
        useOrderIndexMatch: boolean,
    ): IDialogPromptSentence | undefined => {
        const conversationOrderIndex = conversationSentence.order_index;
        if (useOrderIndexMatch) {
            return savedSentenceList.find((savedSentence) =>
                savedSentence.order_index != null
                && conversationOrderIndex != null
                && Number(savedSentence.order_index) === Number(conversationOrderIndex)
            );
        }
        return savedSentenceList[index];
    }, []);

    React.useEffect(() => {
        DialogPromptConversationApi.getAll()
            .then(response => {
                const list = response.data as IDialogPromptConversation[];
                const sortedListDescending = list.sort((a, b) => b.id - a.id);
                setDialogPromptConversationArray(sortedListDescending)
            })
            .catch(err => {
                setDialogPromptConversationArray([])
                handleErrorMessage(err, setErrorMessage, history, location);
            })
    }, [history, location, setDialogPromptConversationArray, setErrorMessage])

    React.useEffect(() => {
        initializedConversationKeyRef.current = null;
        DialogPromptApi.getById(oldDialogPromptId)
            .then((response) => {
                const oldDialogPrompt = response.data as DialogPromptConversationRelated;
                const conversationId = oldDialogPrompt.dialog_prompt_conversation_id ? oldDialogPrompt.dialog_prompt_conversation_id : -1;
                setDialogPromptConversationIdSelected(conversationId);
                setOldDialogPrompt(oldDialogPrompt)
                if (conversationId > 0) {
                    DialogPromptConversationApi.getById(conversationId)
                        .then((conversationResponse) => {
                            upsertDialogPromptConversation(conversationResponse.data as IDialogPromptConversation);
                        })
                        .catch(err => {
                            handleErrorMessage(err, setErrorMessage, history, location);
                        })
                }
            })
            .catch(err => {
                handleErrorMessage(err, setErrorMessage, history, location);
            })
        DialogPromptConversationApi.getAll()
            .then(response => {
                const list = response.data as IDialogPromptConversation[];
                const sortedListDescending = list.sort((a, b) => b.id - a.id);
                setDialogPromptConversationArray(sortedListDescending)
            })
            .catch(err => {
                setDialogPromptConversationArray([])
                handleErrorMessage(err, setErrorMessage, history, location);
            })
        clearConversationErrors()
    }, [oldDialogPromptId, clearConversationErrors, history, location, setDialogPromptConversationArray, setDialogPromptConversationIdSelected, setErrorMessage, setOldDialogPrompt, upsertDialogPromptConversation])

    const setDialogPromptFields = React.useCallback((conversation: IDialogPromptConversation, oldDialogPrompt: DialogPromptConversationRelated | null) => {
        setConversationFields(conversation.dialog_prompt_sentence_list)

        if (dialogPromptClassName == "DialogPromptConversationReadWithBlankSpeakModel") {
            const sentenceList = conversation.dialog_prompt_sentence_list;
            const oldSentenceList = oldDialogPrompt && oldDialogPrompt.dialog_prompt_conversation_id == conversation.id
                ? (oldDialogPrompt as DialogPromptConversationReadWithBlankSpeak).dialog_prompt_sentence_list
                : [];
            const useOrderIndexMatch = oldSentenceList.length > 0
                && getOrderIndexKey(oldSentenceList) === getOrderIndexKey(sentenceList);

            if (sentenceList) {
                const textList = sentenceList.map((sentence, index) => {
                    const savedSentence = oldSentenceList
                        ? getSavedBlankSentence(oldSentenceList, sentence, index, useOrderIndexMatch)
                        : undefined;
                    return savedSentence?.sentence_text_alternate == undefined
                        ? (sentence.sentence_text == undefined ? "" : sentence.sentence_text)
                        : savedSentence.sentence_text_alternate;
                });
                setSentenceBlankList(textList);
                setSentenceBlankErrorList(Array.from({ length: textList.length }, () => ''))
            } else {
                setSentenceBlankList([])
                setSentenceBlankErrorList([])
            }
        } else {
            setSentenceBlankList([])
            setSentenceBlankErrorList([])
        }
    }, [dialogPromptClassName, getOrderIndexKey, getSavedBlankSentence, setConversationFields, setSentenceBlankList, setSentenceBlankErrorList])

    React.useEffect(() => {
        setConversationEnabled(dialogPromptConversationIdSelected <= 0)
        if (dialogPromptConversationIdSelected > 0) {
            const conversation = dialogPromptConversationArray.find(conversation => conversation.id == dialogPromptConversationIdSelected)
            const initializeKey = `${oldDialogPromptId}:${dialogPromptClassName}:${dialogPromptConversationIdSelected}`;
            if (conversation && oldDialogPromptId > 0 && oldDialogPrompt == null) {
                return;
            }
            if (conversation && initializedConversationKeyRef.current !== initializeKey) {
                initializedConversationKeyRef.current = initializeKey;
                setDialogPromptFields(conversation, oldDialogPrompt)
            }
        }
    }, [dialogPromptConversationIdSelected, dialogPromptConversationArray, oldDialogPrompt, dialogPromptClassName, oldDialogPromptId, setDialogPromptFields])

    const handleConversationSelectionChange = (event: { target: { value: unknown } }) => {
        const selectedId = Number(event.target.value);
        initializedConversationKeyRef.current = null;
        setDialogPromptConversationIdSelected(selectedId);
        if (selectedId <= 0) {
            clearConversationFields();
            setSentenceBlankList([]);
            setSentenceBlankErrorList([]);
        }
    };


    const selectedConversation = React.useMemo(
        () => dialogPromptConversationArray.find(
            (dialogPrompt) => dialogPrompt.id === Number(dialogPromptConversationIdSelected)
        ) ?? null,
        [dialogPromptConversationArray, dialogPromptConversationIdSelected]
    );

    const activeConversationSentenceList = React.useMemo(() => {
        if (selectedConversation) {
            return selectedConversation.dialog_prompt_sentence_list ?? [];
        }
        if (dialogPromptConversationIdSelected <= 0) {
            return conversationText.split("\n").map((sentenceText, index) => ({
                id: 0,
                dialog_prompt_id: id,
                sentence_id: 0,
                sentence_text: sentenceText,
                speaker_name: index % 2 === 0 ? SPEAKER_NAME_A : SPEAKER_NAME_B,
                order_index: index
            } as IDialogPromptSentence));
        }
        return [];
    }, [conversationText, dialogPromptConversationIdSelected, id, selectedConversation]);

    const validateSentenceBlankList = React.useCallback((blankList: string[]) => {
        const selectedSentenceList = activeConversationSentenceList;

        const errorList = Array.from({ length: Math.max(blankList.length, selectedSentenceList.length) }, () => '');
        if (dialogPromptConversationIdSelected > 0 && selectedConversation == null) {
            return ['Conversation is still loading'];
        }
        if (blankList.length !== selectedSentenceList.length) {
            const message = `Expected ${selectedSentenceList.length} blank row${selectedSentenceList.length === 1 ? '' : 's'}, found ${blankList.length}`;
            return errorList.map(() => message);
        }

        selectedSentenceList.forEach((sentence, index) => {
            const originalSentence = sentence.sentence_text ?? '';
            const blankSentence = blankList[index] ?? '';
            if (originalSentence.length === 0) {
                errorList[index] = 'Missing sentence';
            } else if (originalSentence.length !== blankSentence.length) {
                errorList[index] = 'Not same length';
            } else {
                for (let i = 0; i < originalSentence.length; i++) {
                    if (originalSentence[i] !== blankSentence[i] && blankSentence[i] !== "*") {
                        errorList[index] = 'Mismatch character / Not star';
                        break;
                    }
                }
            }
        });

        return errorList;
    }, [activeConversationSentenceList, dialogPromptConversationIdSelected, selectedConversation]);

    // Function to handle changes in text inputs
    const handleSentenceBlankInputChange = (index: number, event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
        const updatedValues = [...sentenceBlankList];
        updatedValues[index] = event.target.value;
        setSentenceBlankList(updatedValues);
        setSentenceBlankErrorList(Array.from({ length: updatedValues.length }, () => ''))
    };

    const handleSentenceBlankInputValidation = (index: number) => {
        const errorList = validateSentenceBlankList(sentenceBlankList);
        setSentenceBlankErrorList((prevList) => {
            const updatedList = [...prevList];
            updatedList[index] = errorList[index] ?? '';
            return updatedList;
        });
    }

    React.useEffect(() => {
        let dialogPrompt = null;
        if (dialogPromptClassName == "DialogPromptConversationListenSpeakModel") {
            dialogPrompt = new DialogPromptConversationListenSpeak(
                id,
                dialogId,
                orderIndex,
                isTeacher,
                prompt,
                isNoteToStudent,
                isHidden,
                yesResponse,
                noResponse,
                Number(dialogPromptConversationIdSelected),
            );
        } else if (dialogPromptClassName == "DialogPromptConversationListenWithoutCharacterModel") {
            // do nthing, just skip error
        } else if (dialogPromptClassName == "DialogPromptConversationReadSpeakModel") {
            dialogPrompt = new DialogPromptConversationReadSpeak(
                id,
                dialogId,
                orderIndex,
                isTeacher,
                prompt,
                isNoteToStudent,
                isHidden,
                yesResponse,
                noResponse,
                Number(dialogPromptConversationIdSelected),
            );
        } else if (dialogPromptClassName == "DialogPromptConversationReadWithBlankSpeakModel") {
            const blankErrorList = validateSentenceBlankList(sentenceBlankList);
            if (blankErrorList.some(Boolean)) {
                setSentenceBlankErrorList(blankErrorList);
                setNewDialogPrompt(null);
                return;
            }
            const dialogPromptSentenceList: IDialogPromptSentence[] = sentenceBlankList.map((sentenceBlank: string, index) => ({
                id: -1,
                dialog_prompt_id: -1,
                sentence_id: -1,
                sentence_text_alternate: sentenceBlank,
                speaker_name: activeConversationSentenceList[index]?.speaker_name ?? (index % 2 === 0 ? SPEAKER_NAME_A : SPEAKER_NAME_B),
                order_index: activeConversationSentenceList[index]?.order_index ?? index
            }));
            dialogPrompt = new DialogPromptConversationReadWithBlankSpeak(
                id,
                dialogId,
                orderIndex,
                isTeacher,
                prompt,
                isNoteToStudent,
                isHidden,
                yesResponse,
                noResponse,
                Number(dialogPromptConversationIdSelected),
                dialogPromptSentenceList
            );
        } else if (dialogPromptClassName == "DialogPromptConversationSpeakOnlyModel") {
            dialogPrompt = new DialogPromptConversationSpeakOnly(
                id,
                dialogId,
                orderIndex,
                isTeacher,
                prompt,
                isNoteToStudent,
                isHidden,
                yesResponse,
                noResponse,
                Number(dialogPromptConversationIdSelected),
            );
        } else if (dialogPromptClassName == "DialogPromptConversationListenModel") {
            // do nothing. just skip error.
        } else {
            throw new Error("Unknown class name " + dialogPromptClassName);
        }
        setNewDialogPrompt(dialogPrompt);
    }, [activeConversationSentenceList, dialogPromptClassName, id, dialogId, orderIndex, isTeacher, prompt, isNoteToStudent, isHidden, yesResponse, noResponse, dialogPromptConversationIdSelected, sentenceBlankList, setNewDialogPrompt, validateSentenceBlankList])

    React.useEffect(() => {
        if (dialogPromptConversationIdSelected <= 0) {
            // Convert newline-separated string to array
            const dialogPromptSentenceList: IDialogPromptSentence[] = conversationText.split("\n").map((response: string, index: number) => {
                const pinyinRaw = conversationPinyinList.length > index ? conversationPinyinList[index] : '';
                const pinyinList = pinyinRaw.trim().split(/\s+/).filter(Boolean);
                return {
                    id: 0,
                    dialog_prompt_id: id,
                    sentence_id: 0,
                    sentence_text: response,
                    sentence_text_pinyin: pinyinList,
                    speaker_name: index % 2 === 0 ? SPEAKER_NAME_A : SPEAKER_NAME_B,
                    order_index: index
                }
            });
            const conversation: IDialogPromptConversation = {
                id: 0,
                name: null,
                dialog_prompt_sentence_list: dialogPromptSentenceList
            };
            setNewDialogPromptConversation(conversation)
        } else {
            setNewDialogPromptConversation(null)
        }
        setDialogPromptRequiredError(conversationText.trim().length == 0)
    }, [conversationText, conversationPinyinList, dialogPromptConversationIdSelected, id, setNewDialogPromptConversation, setDialogPromptRequiredError])

    React.useEffect(() => {
        if (dialogPromptConversationIdSelected <= 0 && conversationText) {
            const newSentenceBlankList: string[] = conversationText.split("\n")
            setSentenceBlankList(newSentenceBlankList)
            setSentenceBlankErrorList(Array.from({ length: newSentenceBlankList.length }, () => ''))
        }
    }, [conversationText, dialogPromptConversationIdSelected])

    return (
        <Stack spacing={4} sx={{ width: '90%', margin: '0 auto', pt: 2, pb: 4 }}>
            <FormControl fullWidth variant="outlined">
                <InputLabel>Select Conversation</InputLabel>
                <Select
                    required
                    label="Select Conversation"
                    value={dialogPromptConversationIdSelected}
                    onChange={handleConversationSelectionChange}
                // onChange={handleDialogPromptConversationListenIdChange}
                >
                    <MenuItem key={-1} value={-1}>New Conversation</MenuItem>
                    {dialogPromptConversationArray
                        .filter(conversation => conversation.dialog_prompt_sentence_list && conversation.dialog_prompt_sentence_list.length > 0) // Filter out null or empty lists
                        .map((conversation, index) => (
                            <MenuItem key={index} value={String(conversation.id)}>
                                {conversation.dialog_prompt_sentence_list
                                    .slice(0, 2) // Take only the first two items
                                    .map(item => item.sentence_text) // Extract the sentence_text
                                    .join(" / ") // Join them with " / "
                                }
                            </MenuItem>
                        ))}
                </Select>
                <FormHelperText style={{ color: dialogPromptRequiredError ? 'red' : 'inherit' }}>
                    Conversation is required!
                </FormHelperText>
            </FormControl>

            <div>
                <TextField
                    label="Conversation"
                    value={conversationText}
                    onChange={handleConversationTextChange}
                    multiline
                    rows={6}
                    sx={{ width: '100%' }}
                    required
                    disabled={!conversationEnabled}
                />
                <Typography sx={{ color: 'grey.500', fontSize: '0.9rem' }}>
                    Alternate lines between speaker A and B
                </Typography>
            </div>
            <TextField
                label="Conversation Pinyin"
                value={conversationPinyin}
                onChange={handleConversationPinyinChange}
                multiline
                rows={6}
                sx={{ width: '100%' }}
                required
                disabled={!conversationEnabled}
                error={conversationErrorText != null}
                helperText={conversationErrorText}
            />

            {(dialogPromptClassName == "DialogPromptConversationReadWithBlankSpeakModel") && (<>
                <div>
                    Replace any characters with star *
                </div>
                <div>
                    <ul>
                        {sentenceBlankList.map((sentenceBlank, index) => (
                            <li key={index}>
                                {index % 2 === 0 ? 'A: ' : 'B: '}
                                <TextField
                                    variant="outlined"
                                    value={sentenceBlankList[index] || ''}
                                    onChange={(event) => handleSentenceBlankInputChange(index, event)}
                                    onBlur={() => handleSentenceBlankInputValidation(index)}
                                    error={Boolean(sentenceBlankErrorList[index])} // Set error prop based on error presence
                                    helperText={sentenceBlankErrorList[index]} // Display error message
                                />
                            </li>
                        ))}
                    </ul>
                </div>
            </>)}
        </Stack>
    )
}
